import React, { ElementType } from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SxProps } from '@mui/material';

import useShrinkFieldState from '../../hooks/useShrinkFieldState';
import { Field } from '../../lib/recoil/Form';

interface AppSelectProps<T> {
  field: Field<T | null>;
  id: string;
  label: string;
  defaultValue?: T;  // Optional to handle cases where no default is provided
  disabled?: boolean;
  options: T[];
  getOptionKey: (item: T) => string | number;
  getOptionLabel: (item: T) => string;
  getOptionByKey: (optionKey: unknown) => T | null;
  getDisabledOptionByKey?: (item: T) => boolean;
  dataCy?: string;
  formControlSx?: SxProps;
  error?: boolean;
}

const AppSelect: ElementType = <T extends unknown>({
  field,
  id,
  label,
  options,
  defaultValue,
  dataCy = '',
  disabled = false,
  getOptionKey,
  getOptionLabel,
  getOptionByKey,
  getDisabledOptionByKey = () => false,
  formControlSx = {},
  error: externalError = false,
}: AppSelectProps<T>) => {
  const { value, setValue, error, helperText, touched, shrink, setFocused } = useShrinkFieldState<T | null>(field, defaultValue);

  const selectedValue = value ? getOptionKey(value) : '';

  return (
    <FormControl
      error={externalError ?? (touched && error)}
      fullWidth
      sx={formControlSx}
      variant='outlined'
    >
      <InputLabel shrink={shrink}>{label}</InputLabel>
      <Select
        data-cy={dataCy}
        disabled={disabled}
        id={id}
        onBlur={() => setFocused(false)}
        onChange={
          (event) => {
            const selectedOption = getOptionByKey(event.target.value);
            setValue(selectedOption);
          }
        }
        onFocus={() => setFocused(true)}
        value={selectedValue}
        variant='outlined'
      >
        {
          options.map((item: T) => (
            <MenuItem
              disabled={getDisabledOptionByKey(item)}
              key={getOptionKey(item)}
              value={getOptionKey(item)}
            >
              {getOptionLabel(item)}
            </MenuItem>
          ))
        }
      </Select>
      {
        helperText && (
          <FormHelperText>{helperText}</FormHelperText>
        )
      }
    </FormControl>
  );
};

export default AppSelect;
